import React from 'react';

/**
 * AdPlacementPreview
 * A purely presentational, abstracted "wireframe" vignette of the SaveDelete
 * homepage and /ai-tools/ directory, with the paid ad zones highlighted and
 * labelled. No data fetching, no images — just stylized skeleton blocks.
 */

const SkelLine: React.FC<{ w?: string; className?: string }> = ({ w = 'w-full', className = '' }) => (
  <div className={`h-2 rounded bg-slate-200 ${w} ${className}`} />
);

const StoryRow: React.FC = () => (
  <div className="flex items-center gap-3">
    <div className="h-10 w-14 shrink-0 rounded bg-slate-200" />
    <div className="flex-1 space-y-1.5">
      <SkelLine w="w-5/6" />
      <SkelLine w="w-2/3" />
    </div>
  </div>
);

const AdPlacementPreview: React.FC = () => {
  return (
    <div className="space-y-10">
      {/* ---- Vignette 1: Homepage with Sponsor Post ---- */}
      <div>
        {/* Browser frame */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          {/* Fake top bar */}
          <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
            <span className="h-3 w-3 rounded-full bg-red-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-green-400" />
            <div className="ml-3 flex-1 max-w-xs">
              <div className="rounded-full bg-white border border-slate-200 px-3 py-1 text-[11px] text-slate-400 text-center">
                savedelete.com
              </div>
            </div>
          </div>

          {/* Page body */}
          <div className="p-4 md:p-5">
            {/* Header nav */}
            <div className="flex items-center gap-3 mb-5">
              <div className="h-6 w-24 rounded bg-slate-300" />
              <div className="ml-auto hidden sm:flex items-center gap-2">
                <div className="h-4 w-12 rounded-full bg-slate-200" />
                <div className="h-4 w-12 rounded-full bg-slate-200" />
                <div className="h-4 w-12 rounded-full bg-slate-200" />
              </div>
            </div>

            {/* Two-column layout */}
            <div className="flex flex-col md:flex-row gap-5">
              {/* LEFT: news river */}
              <div className="flex-1 space-y-4 animate-pulse">
                <StoryRow />
                <StoryRow />
                <StoryRow />
                <StoryRow />
                <StoryRow />
              </div>

              {/* RIGHT: rail */}
              <div className="w-full md:w-52 shrink-0 space-y-4">
                {/* Highlighted Sponsor Post slot */}
                <div className="relative rounded-xl ring-2 ring-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.15)] bg-amber-50/40 p-3">
                  <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-amber-950 shadow">
                    ① Sponsor Post
                  </span>
                  <div className="pt-1.5">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-600 mb-1.5">
                      Sponsored
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-7 w-7 rounded bg-amber-300" />
                      <div className="h-3 w-16 rounded bg-amber-300" />
                    </div>
                    <div className="space-y-1.5">
                      <div className="h-2 rounded bg-amber-200 w-full" />
                      <div className="h-2 rounded bg-amber-200 w-4/5" />
                    </div>
                    <div className="mt-2 text-[9px] font-medium text-amber-700/70">Your brand</div>
                  </div>
                </div>

                {/* More rail skeleton blocks */}
                <div className="space-y-3 animate-pulse">
                  <div className="rounded-lg border border-slate-100 p-3 space-y-1.5">
                    <SkelLine w="w-1/2" className="!bg-slate-300" />
                    <SkelLine w="w-full" />
                    <SkelLine w="w-3/4" />
                  </div>
                  <div className="rounded-lg border border-slate-100 p-3 space-y-1.5">
                    <SkelLine w="w-1/2" className="!bg-slate-300" />
                    <SkelLine w="w-full" />
                    <SkelLine w="w-2/3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legend / callout */}
        <p className="mt-3 text-sm text-slate-600">
          <span className="font-semibold text-amber-700">①</span> Your{' '}
          <strong>Sponsor Post</strong> — top of the homepage rail, right next to the day's top
          stories.
        </p>
      </div>

      {/* ---- Vignette 2: Featured AI Tool mini-grid ---- */}
      <div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
            <span className="h-3 w-3 rounded-full bg-red-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-green-400" />
            <div className="ml-3 flex-1 max-w-xs">
              <div className="rounded-full bg-white border border-slate-200 px-3 py-1 text-[11px] text-slate-400 text-center">
                savedelete.com/ai-tools/
              </div>
            </div>
          </div>

          <div className="p-4 md:p-5">
            {/* Highlighted Featured card */}
            <div className="relative rounded-xl ring-2 ring-blue-500 shadow-[0_0_0_4px_rgba(59,130,246,0.15)] bg-blue-50/40 p-3 mb-4">
              <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white shadow">
                ② Featured AI Tool
              </span>
              <div className="pt-1.5 flex items-center gap-3">
                <div className="h-11 w-11 shrink-0 rounded-lg bg-blue-300" />
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-24 rounded bg-blue-300" />
                    <span className="inline-flex items-center rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
                      ⭐ Featured
                    </span>
                  </div>
                  <div className="h-2 w-full rounded bg-blue-200" />
                  <div className="h-2 w-3/4 rounded bg-blue-200" />
                </div>
              </div>
            </div>

            {/* Regular grid skeleton */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg border border-slate-100 p-3 space-y-2">
                  <div className="h-8 w-8 rounded-lg bg-slate-200" />
                  <SkelLine w="w-3/4" className="!bg-slate-300" />
                  <SkelLine w="w-full" />
                  <SkelLine w="w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-3 text-sm text-slate-600">
          <span className="font-semibold text-blue-700">②</span> Your{' '}
          <strong>Featured AI Tool</strong> — pinned to the Featured row with a badge and top-of-category
          placement in the directory.
        </p>
      </div>
    </div>
  );
};

export default AdPlacementPreview;
